/**
 * Document Manager
 * Handles PDF and document management functionality
 */

import { logger } from '../utils/logger'
import { ApiService } from '../services/api'
import { AppState } from '../stores/app-state'
import type { PDFFile } from '../types'

export class DocumentManager {
  private apiService: ApiService
  private appState: AppState
  private pdfViewer: any = null

  constructor(apiService: ApiService, appState: AppState) {
    this.apiService = apiService
    this.appState = appState
    logger.debug('DocumentManager initialized')
  }

  /**
   * Set PDF viewer instance
   */
  public setPDFViewer(pdfViewer: any): void {
    this.pdfViewer = pdfViewer
    logger.debug('PDF viewer set in DocumentManager')
  }

  /**
   * Load project documents
   */
  public async loadProjectDocuments(): Promise<void> {
    const currentProject = this.appState.getCurrentProject()
    if (!currentProject) {
      logger.warn('No current project selected for loading documents')
      return
    }

    try {
      logger.info('Loading project documents for project:', currentProject.id)
      
      // Use new multi-document API
      const response = await this.apiService.getProjectDocuments(currentProject.id)
      logger.info('API response for getProjectDocuments:', response)
      
      const documents = response.documents || []
      logger.info('Extracted documents array:', documents)
      logger.info('Documents length:', documents.length)
      
      this.appState.setDocuments(documents)
      logger.info(`Loaded ${documents.length} documents for project ${currentProject.id}`)
    } catch (error) {
      logger.error('Failed to load project documents:', error)
      this.appState.setDocuments([])
      throw error
    }
  }

  /**
   * Load document for selection
   */
  public async loadDocumentForSelection(selection: any): Promise<void> {
    try {
      logger.debug('Loading document for selection:', selection.id)
      
      // Use document_id for multi-document architecture
      const documentId = selection.document_id
      if (!documentId) {
        logger.warn('Selection has no document ID:', selection.id)
        return
      }

      const documents = this.appState.getProjectDocuments()
      const projectDoc = documents.find(doc => doc.id === documentId)
      
      if (projectDoc) {
        // Route to appropriate viewer based on document type
        if (projectDoc.document_type === 'pdf') {
          // Convert ProjectDocument to PDFFile format
          const pdfFile = {
            id: projectDoc.id,
            project_id: '', // ProjectDocument doesn't have project_id
            filename: projectDoc.original_filename,
            pageCount: projectDoc.page_count || 1,
            dimensions: { width: 0, height: 0 } // ProjectDocument doesn't have dimensions
          }
          this.showPDFViewer(pdfFile)
        } else if (projectDoc.document_type === 'image') {
          logger.debug('Image document found for selection:', selection.id)
          
          // Convert ProjectDocument to image file format
          const imageFile = {
            id: projectDoc.id,
            project_id: this.appState.getCurrentProject()?.id || '',
            filename: projectDoc.original_filename,
            document_type: 'image'
          }
          
          // Show image viewer
          this.showImageViewer(imageFile)
        } else {
          logger.debug('Manual entry document found for selection:', selection.id)
        }
        logger.debug('Document loaded for selection:', selection.id)
      } else {
        logger.warn('Document not found for selection:', selection.id, 'Document ID:', documentId)
      }
    } catch (error) {
      logger.error('Failed to load document for selection:', error)
      throw error
    }
  }

  /**
   * Show PDF viewer
   */
  public showPDFViewer(file: PDFFile): void {
    try {
      logger.debug('Showing PDF viewer for file:', file.filename)
      
      // Try both standard and unified container IDs
      const containerIds = ['pdf-viewer-container', 'unified-pdf-viewer-container']
      let container: HTMLElement | null = null
      
      for (const id of containerIds) {
        container = document.getElementById(id)
        if (container) break
      }
      
      if (!container) {
        logger.error('PDF viewer container not found')
        return
      }

      // Initialize PDF viewer if not already done
      if (!this.pdfViewer) {
        const { PDFViewer } = require('../components/pdf-viewer')
        this.pdfViewer = new PDFViewer(container, this.apiService, this.appState)
        logger.debug('PDF viewer initialized')
      }

      // Load the PDF file
      this.pdfViewer.loadPDF(file)
      
      // Show the container
      container.classList.remove('hidden')
      
      logger.debug('PDF viewer shown for file:', file.filename)
    } catch (error) {
      logger.error('Failed to show PDF viewer:', error)
      throw error
    }
  }

  /**
   * Show image viewer
   */
  public showImageViewer(file: any): void {
    try {
      logger.debug('Showing image viewer for:', file.filename)
      
      // Dispatch event to open image viewer
      const event = new CustomEvent('document-auto-display-requested', {
        detail: { document: file }
      })
      window.dispatchEvent(event)
      
    } catch (error) {
      logger.error('Failed to show image viewer:', error)
      throw error
    }
  }

  /**
   * Hide PDF viewer
   */
  public hidePDFViewer(): void {
    try {
      logger.debug('Hiding PDF viewer')
      
      // Hide all PDF viewer containers
      const containerIds = ['pdf-viewer-container', 'unified-pdf-viewer-container', 'modal-pdf-viewer']
      containerIds.forEach(id => {
        const container = document.getElementById(id)
        if (container) {
          container.classList.add('hidden')
          logger.debug(`Hidden PDF viewer container: ${id}`)
        }
      })
      
      logger.debug('PDF viewer hidden successfully')
    } catch (error) {
      logger.error('Failed to hide PDF viewer:', error)
    }
  }

  /**
   * Ensure gallery initialization
   */
  public ensureGalleryInitialization(): void {
    logger.debug('Ensuring gallery initialization')
    
    // Check if gallery is already initialized
    const existingGallery = document.querySelector('.selections-gallery')
    if (existingGallery) {
      logger.debug('Gallery already initialized')
      return
    }

    // Initialize gallery if needed
    const galleryContainer = document.getElementById('selections-gallery-container-main')
    if (galleryContainer) {
      // Gallery will be initialized by the main App class
      logger.debug('Gallery container found, initialization delegated to App class')
    } else {
      logger.warn('Gallery container not found')
    }
  }

  /**
   * Get PDF name by ID
   */
  public getPDFNameById(pdfId: string): string {
    const documents = this.appState.getProjectDocuments()
    const doc = documents.find(d => d.id === pdfId)
    return doc?.original_filename || 'Unknown PDF'
  }

  /**
   * Get document name by ID
   */
  public getDocumentName(pdfId: string): string {
    return this.getPDFNameById(pdfId)
  }

  /**
   * Select first available PDF
   */
  public async selectFirstAvailablePDF(): Promise<void> {
    try {
      const documents = this.appState.getProjectDocuments()
      
      if (documents.length === 0) {
        logger.warn('No documents available to select')
        return
      }

      const firstPDF = documents[0]
      logger.debug('Selecting first available PDF:', firstPDF.original_filename)
      
      // Convert ProjectDocument to PDFFile format
      const currentProject = this.appState.getCurrentProject()
      const pdfFile = {
        id: firstPDF.id,
        project_id: currentProject?.id || '', // Get project_id from current project
        filename: firstPDF.original_filename,
        pageCount: firstPDF.page_count || 1,
        dimensions: { width: 0, height: 0 } // ProjectDocument doesn't have dimensions
      }
      
      // Only show PDF viewer if we're in a modal context
      // Check if modal PDF viewer container exists
      const modalContainer = document.getElementById('modal-pdf-viewer')
      if (modalContainer) {
        this.showPDFViewer(pdfFile)
      } else {
        logger.debug('No PDF viewer container found, skipping PDF display')
        // Just set the current file without showing the viewer
        this.appState.setCurrentFile(pdfFile)
      }
    } catch (error) {
      logger.error('Failed to select first available PDF:', error)
      throw error
    }
  }

  /**
   * Handle modal file upload
   */
  public async handleModalFileUpload(file: File): Promise<void> {
    try {
      logger.info('Handling modal file upload:', file.name)
      
      const currentProject = this.appState.getCurrentProject()
      if (!currentProject) {
        throw new Error('No current project selected')
      }

      logger.info('Uploading file to project:', currentProject.id, currentProject.name)

      // Upload file using unified document API
      const uploadResponse = await this.apiService.uploadDocument(file, currentProject.id)
      logger.info('Upload response:', uploadResponse)
      
      logger.info('File uploaded successfully:', file.name)
      
      // Refresh project documents
      await this.loadProjectDocuments()
      
      // Auto-display the uploaded PDF
      const uploadedDoc = this.appState.getProjectDocuments().find(doc => doc.original_filename === file.name)
      if (uploadedDoc) {
        logger.info('Auto-displaying uploaded document:', uploadedDoc.original_filename)
        logger.info('Uploaded document data:', uploadedDoc)
        
        // Convert ProjectDocument to file format
        let fileObject: any = null
        if (uploadedDoc.document_type !== 'image') {
          // PDF file format
          fileObject = {
            id: uploadedDoc.id,
            project_id: currentProject.id,
            filename: uploadedDoc.original_filename,
            pageCount: uploadedDoc.page_count || 1, // Fallback to 1 if page_count is undefined
            dimensions: {
              width: 0, // Will be populated when PDF is loaded
              height: 0
            }
          }
          
          logger.info('Created PDF file object:', fileObject)
        } else {
          // Image file format
          fileObject = {
            id: uploadedDoc.id,
            project_id: currentProject.id,
            filename: uploadedDoc.original_filename,
            document_type: 'image'
          }
          
          logger.info('Created image file object:', fileObject)
        }
        
        // CRITICAL FIX: Set current file for both PDFs and images
        this.appState.setCurrentFile(fileObject)
        
        // Refresh modal document selector to show the newly uploaded document
        const modalManager = (window as any).appInstance?.modalManager || 
                           (window as any).app?.modalManager ||
                           (this.appState as any).modalManager
        if (modalManager && modalManager.refreshModalDocumentSelector) {
          logger.info('Refreshing modal document selector with uploaded document:', uploadedDoc.id)
          modalManager.refreshModalDocumentSelector(undefined, uploadedDoc.id)
        }
        
        // Dispatch appropriate event based on document type
        if (uploadedDoc.document_type === 'image') {
          console.log('🚀 Dispatching document auto-display event for image:', uploadedDoc.original_filename)
          const event = new CustomEvent('document-auto-display-requested', {
            detail: { document: uploadedDoc }
          })
          window.dispatchEvent(event)
          console.log('✅ Document auto-display event dispatched')
        } else if (fileObject) { // For PDFs and other document types
          console.log('🚀 Dispatching PDF auto-display event for:', fileObject.filename)
          const event = new CustomEvent('pdf-auto-display-requested', {
            detail: { pdfFile: fileObject }
          })
          window.dispatchEvent(event)
          console.log('✅ PDF auto-display event dispatched')
        }
      } else {
        logger.warn('Uploaded PDF not found in document list for auto-display')
      }
      
      // Show success message
      logger.info(`File "${file.name}" uploaded successfully`)
    } catch (error) {
      logger.error('Failed to handle modal file upload:', error)
      throw error
    }
  }

  /**
   * Initialize modal PDF viewer
   */
  public async initializeModalPDFViewer(pdfFile: any, skipAutoSelections: boolean = false, _forceReload: boolean = false): Promise<void> {
    try {
      logger.debug('Initializing modal PDF viewer for file:', pdfFile.original_filename)
      
      // This would typically initialize a PDF viewer within a modal
      // The actual implementation would depend on the specific PDF viewer component
      
      if (!skipAutoSelections) {
        // Load selections for this PDF
        const response = await this.apiService.listPDFSelections(pdfFile.id)
        const selections = response.selections
        if (selections && selections.length > 0) {
          logger.debug(`Loaded ${selections.length} selections for PDF:`, pdfFile.filename)
        }
      }
      
      logger.debug('Modal PDF viewer initialized successfully')
    } catch (error) {
      logger.error('Failed to initialize modal PDF viewer:', error)
      throw error
    }
  }

  /**
   * Get document statistics
   */
  public getDocumentStatistics(): any {
    const documents = this.appState.getProjectDocuments()
    const selections = this.appState.getSelections()
    
    const stats = {
      totalDocuments: documents.length,
      totalSelections: selections.length,
      selectionsPerDocument: {} as any,
      averageSelectionsPerDocument: 0
    }
    
    // Calculate selections per document
    documents.forEach(doc => {
      const docSelections = selections.filter(sel => sel.document_id === doc.id)
      stats.selectionsPerDocument[doc.id] = docSelections.length
    })
    
    // Calculate average
    if (documents.length > 0) {
      stats.averageSelectionsPerDocument = selections.length / documents.length
    }
    
    return stats
  }

  /**
   * Validate document integrity
   */
  public validateDocumentIntegrity(): { isValid: boolean; errors: string[] } {
    const errors: string[] = []
    const documents = this.appState.getProjectDocuments()
    const selections = this.appState.getSelections()
    
    // Check for selections referencing non-existent documents
    const documentIds = new Set(documents.map(d => d.id))
    const invalidSelections = selections.filter(s => s.document_id && !documentIds.has(s.document_id))
    
    if (invalidSelections.length > 0) {
      errors.push(`Found ${invalidSelections.length} selections referencing non-existent documents`)
    }
    
    return {
      isValid: errors.length === 0,
      errors
    }
  }

  /**
   * Cleanup resources
   */
  public cleanup(): void {
    if (this.pdfViewer) {
      this.pdfViewer.cleanup?.()
      this.pdfViewer = null
    }
    logger.debug('DocumentManager cleaned up')
  }
}
